import { getPreImage } from "./ClientService";

/**
 * Optimizes an image to WebP format with a maximum size of 250KB while preserving quality.
 * Uses canvas-based resizing and iterative quality adjustment for optimal results.
 * 
 * @param {File} file - The original image file.
 * @returns {Promise<string>} - Optimized WebP image as Data URL.
 */
export async function optimizeAndConvertToWebp(file) {
    try {
        const webpDataUrl = await convertToWebpWithQuality(file, 250);
        const sizeInKB = getSizeInKB(webpDataUrl);
        console.log("Optimized WebP size:", sizeInKB, "KB", webpDataUrl);
        return webpDataUrl;
    } catch (error) {
        console.error("Image processing error:", error);
        throw error;
    }
}

/** Higher-fidelity stills for Cinematic Studio (larger budget, up to 3840px). */
export async function optimizeCinematicStill(file) {
    try {
        const webpDataUrl = await convertToWebpWithQuality(file, 1600, 3840);
        return webpDataUrl;
    } catch (error) {
        console.error("Cinematic still processing error:", error);
        throw error;
    }
}

export async function uploadImageToS3Bucket(optimizedFile, fileType = "image/webp", fileName) {
    try {
        // need to place image upload functionality here
        const byteCharacters = atob(optimizedFile.split(",")[1]);
        const byteArrays = [];
        for (let i = 0; i < byteCharacters.length; i++) {
            byteArrays.push(byteCharacters.charCodeAt(i));
        }
        const optimizedBlob = new Blob([new Uint8Array(byteArrays)], {
            type: fileType,
        });
        console.log(optimizedBlob, 7654323456);
        const response = await getPreImage({ fileName, fileType });
        if (response.code !== 200) return false;
        const { uploadUrl, publicUrl } = await response.data;
        await fetch(uploadUrl, {
            method: "PUT",
            body: optimizedBlob,
            headers: { "Content-Type": fileType },
        });
        console.log(publicUrl, 'publicUrl');
        return publicUrl;
    } catch (error) {
        console.error("Image uploading error:", error);
        throw error;
    }
}

/**
 * Converts an image file to WebP with iterative quality adjustment and conditional resizing.
 * 
 * @param {File} file - Image file to process.
 * @param {number} maxSizeKB - Maximum allowed file size in KB.
 * @returns {Promise<string>} - WebP Data URL meeting size constraints.
 */
function convertToWebpWithQuality(file, maxSizeKB, maxInitialDimension = 1920) {
    return new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = function () {
            URL.revokeObjectURL(img.src);
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');

            // Configure high-quality image smoothing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';

            // Initial dimensions calculation
            let width = img.width;
            let height = img.height;

            // Apply initial resize if needed
            if (width > maxInitialDimension || height > maxInitialDimension) {
                const ratio = Math.min(maxInitialDimension / width, maxInitialDimension / height);
                width = Math.floor(width * ratio);
                height = Math.floor(height * ratio);
            }

            canvas.width = width;
            canvas.height = height;
            ctx.drawImage(img, 0, 0, width, height);

            // First pass: Adjust quality without resizing
            let result = tryQualityAdjustment(canvas, maxSizeKB);
            if (result) return resolve(result);

            // Second pass: Gradual resizing with quality adjustment
            result = tryScaling(canvas, ctx, img, width, height, maxSizeKB);
            if (result) return resolve(result);

            // Fallback to lowest quality setting
            resolve(canvas.toDataURL('image/webp', 0.1));
        };

        img.onerror = () => reject(new Error('Failed to load image'));
        img.src = URL.createObjectURL(file);
    });
}

/**
 * Attempts quality adjustment at current dimensions.
 */
function tryQualityAdjustment(canvas, maxSizeKB) {
    let quality = 0.95;
    while (quality >= 0.3) { // Stop at reasonable minimum quality
        const dataUrl = canvas.toDataURL('image/webp', quality);
        if (getSizeInKB(dataUrl) <= maxSizeKB) {
            console.log(`Quality adjustment succeeded at ${quality}`);
            return dataUrl;
        }
        quality -= 0.03; // Fine-grained quality adjustment
    }
    return null;
}

/**
 * Attempts gradual scaling with quality adjustment.
 */
function tryScaling(canvas, ctx, img, originalWidth, originalHeight, maxSizeKB) {
    let scaleFactor = 0.9;
    while (scaleFactor >= 0.5) { // Minimum scale threshold
        const newWidth = Math.floor(originalWidth * scaleFactor);
        const newHeight = Math.floor(originalHeight * scaleFactor);

        canvas.width = newWidth;
        canvas.height = newHeight;
        ctx.drawImage(img, 0, 0, newWidth, newHeight);

        const result = tryQualityAdjustment(canvas, maxSizeKB);
        if (result) {
            console.log(`Scaling succeeded at ${scaleFactor}`);
            return result;
        }

        scaleFactor -= 0.05; // Smaller scaling steps
    }
    return null;
}

/**
 * Calculates Data URL size in KB.
 */
function getSizeInKB(dataUrl) {
    const base64 = dataUrl.split(',')[1];
    // const sizeBytes = new Blob([base64]).size
    const sizeBytes = Math.round((base64.length * 3) / 4);
    return (sizeBytes / 1024).toFixed(2);
    // return sizeBytes/1000;
}